import { DialogElement } from 'theme';

(function () {
  'use strict';

  const LANGUAGE_FLAG_ISO = {
    de: 'DE',
    en: 'GB',
    fr: 'FR',
    nl: 'NL',
    pl: 'PL',
    pt: 'PT',
    'pt-br': 'BR',
    'pt-pt': 'PT',
    it: 'IT',
    es: 'ES',
    da: 'DK',
    sv: 'SE',
    nb: 'NO',
    cs: 'CZ',
    sk: 'SK',
    fi: 'FI',
    el: 'GR',
    tr: 'TR',
    ja: 'JP',
    ko: 'KR',
    'zh-cn': 'CN',
    'zh-tw': 'TW',
  };

  function getLanguageFlagUrl(isoCode) {
    const flagIso =
      LANGUAGE_FLAG_ISO[isoCode] ||
      isoCode.split('-')[0].toUpperCase();
    return `https://cdn.shopify.com/static/images/flags/${flagIso.toLowerCase()}.svg?width=42`;
  }

  function getLanguageFlagHtml(language) {
    const flagUrl = language?.flagUrl || getLanguageFlagUrl(language?.iso);
    return `<img src="${flagUrl}" alt="" width="21" height="16" loading="lazy" class="geolocation-popup__flag">`;
  }

  function getLanguageDisplayName(isoCode, fallbackName) {
    if (fallbackName) {
      return fallbackName;
    }

    try {
      const locale = document.documentElement.lang || 'de';
      return new Intl.DisplayNames([locale], { type: 'language' }).of(isoCode);
    } catch (error) {
      return isoCode.toUpperCase();
    }
  }

  class GeolocationPopup extends DialogElement {
    connectedCallback() {
      super.connectedCallback();

      this.addEventListener(
        'dialog:after-hide',
        () => {
          window.clearTimeout(this._geolocationAutoShowTimerId);
          this._geolocationDismissedThisPage = true;
        },
        { signal: this._abortController.signal }
      );

      this._bindAriaControlsToggle();

      this._marketConfig = this._readMarketConfig();
      this._countryInput = this.querySelector('[data-geolocation-country-input]');
      this._localeInput = this.querySelector('[data-geolocation-locale-input]');
      this._countryListbox = this.querySelector('[data-geolocation-country-listbox]');
      this._languageListbox = this.querySelector('[data-geolocation-language-listbox]');
      this._countryLabel = this.querySelector('[data-geolocation-country-label]');
      this._languageLabel = this.querySelector('[data-geolocation-language-label]');
      this._countryFlag = this.querySelector('[data-geolocation-country-flag]');
      this._languageFlag = this.querySelector('[data-geolocation-language-flag]');
      this._form = this.querySelector('#geolocation-form');

      const backdrop = this.querySelector('.geolocation-popup__backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', () => this.hide(), {
          signal: this._abortController.signal,
        });
      }

      this._initDropdowns();

      this._bindListbox(this._countryListbox, (value) => {
        this._onCountryChange(value);
        this._closeDropdowns();
      });
      this._bindListboxDelegation(this._languageListbox, (value) => {
        this._onLanguageChange(value);
        this._closeDropdowns();
      });

      this._enhanceLanguageLabels();

      this._form?.addEventListener(
        'submit',
        () => {
          try {
            localStorage.setItem('theme:geolocation-confirmed', 'true');
          } catch (error) { }
        },
        { signal: this._abortController.signal }
      );

      if (!Shopify.designMode) {
        if (!this._geolocationAutoShowRan) {
          this._geolocationAutoShowRan = true;
          this._maybeAutoShow();
        }
      }
    }

    _allowOutsideClick() {
      return true;
    }

    get shouldLock() {
      return true;
    }

    get shouldAppendToBody() {
      return true;
    }

    get initialFocus() {
      return this.querySelector('.geolocation-popup__select');
    }

    _bindAriaControlsToggle() {
      document.addEventListener(
        'click',
        (event) => {
          const control = event.target.closest(
            '[aria-controls="geolocation-popup"]'
          );
          if (
            !control ||
            control.getAttribute('aria-controls') !== 'geolocation-popup'
          ) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          const drawer = control.closest('navigation-drawer');

          if (this.open) {
            this._onToggleClicked(event);
            return;
          }

          if (drawer?.open) {
            const afterHide = drawer.hide();
            if (afterHide && typeof afterHide.then === 'function') {
              afterHide.then(() => this.show(event));
            } else {
              this.show(event);
            }
            return;
          }

          this._onToggleClicked(event);
        },
        { capture: true, signal: this._abortController.signal }
      );
    }

    _initDropdowns() {
      this.querySelectorAll('[data-geolocation-dropdown-trigger]').forEach(
        (trigger) => {
          trigger.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopPropagation();
              const field = trigger.closest('[data-geolocation-dropdown]');
              const panel = field?.querySelector(
                '[data-geolocation-listbox-panel]'
              );
              const isOpen = field?.classList.contains(
                'geolocation-popup__field--open'
              );

              if (isOpen) {
                this._closeDropdowns();
                return;
              }

              this._closeDropdowns(field);

              if (panel) {
                field.classList.add('geolocation-popup__field--open');
                panel.hidden = false;
                trigger.setAttribute('aria-expanded', 'true');
              }
            },
            { signal: this._abortController.signal }
          );
        }
      );

      document.addEventListener(
        'click',
        (event) => {
          if (!event.target.closest('[data-geolocation-dropdown]')) {
            this._closeDropdowns();
          }
        },
        { signal: this._abortController.signal }
      );
    }

    _closeDropdowns(exceptField) {
      this.querySelectorAll('[data-geolocation-dropdown]').forEach((field) => {
        if (exceptField && field === exceptField) {
          return;
        }

        field.classList.remove('geolocation-popup__field--open');
        const panel = field.querySelector('[data-geolocation-listbox-panel]');
        const trigger = field.querySelector('[data-geolocation-dropdown-trigger]');

        if (panel) {
          panel.hidden = true;
        }

        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    }

    _updateLanguageIcon(container, localeIso) {
      if (!container || !localeIso) {
        return;
      }

      const countryIso = this._countryInput?.value;
      const language = this._marketConfig[countryIso]?.languages?.find(
        (entry) => entry.iso === localeIso
      );

      container.innerHTML = getLanguageFlagHtml(
        language || { iso: localeIso }
      );
    }

    _updateCountryIcon(container, countryIso) {
      if (!container) {
        return;
      }

      const market = this._marketConfig[countryIso];
      if (!market) {
        return;
      }

      if (market.isInternational) {
        container.innerHTML =
          '<span class="geolocation-popup__icon geolocation-popup__icon--globe" aria-hidden="true"></span>';
        return;
      }

      if (market.flagUrl) {
        container.innerHTML = `<img src="${market.flagUrl}" alt="" width="21" height="16" loading="lazy" class="geolocation-popup__flag">`;
      } else {
        const marketFlag = getLanguageFlagUrl(countryIso);
        container.innerHTML = `<img src="${marketFlag}" alt="" width="21" height="16" loading="lazy" class="geolocation-popup__flag">`;
      }
    }

    _bindListbox(listbox, callback) {
      if (!listbox) {
        return;
      }

      ['listbox:change', 'listbox:select'].forEach((eventName) => {
        listbox.addEventListener(
          eventName,
          (event) => callback(event.detail.value),
          { signal: this._abortController.signal }
        );
      });
    }

    _bindListboxDelegation(listbox, callback) {
      if (!listbox) {
        return;
      }

      listbox.addEventListener(
        'click',
        (event) => {
          const option = event.target.closest('[role="option"]');
          if (!option || !listbox.contains(option)) {
            return;
          }

          event.stopPropagation();
          listbox.setAttribute('aria-activedescendant', option.id);
          callback(option.value);
        },
        { signal: this._abortController.signal }
      );
    }

    _enhanceLanguageLabels() {
      if (this._languageLabel && this._localeInput?.value) {
        const countryIso = this._countryInput?.value;
        const language = this._marketConfig[countryIso]?.languages?.find(
          (entry) => entry.iso === this._localeInput.value
        );
        this._languageLabel.textContent = getLanguageDisplayName(
          this._localeInput.value,
          language?.name
        );
        this._updateLanguageIcon(
          this._languageFlag,
          this._localeInput.value
        );
      }
    }

    _readMarketConfig() {
      const configNode = this.querySelector('#geolocation-market-config');
      if (!configNode) {
        return {};
      }

      try {
        const config = JSON.parse(configNode.textContent);

        Object.values(config).forEach((market) => {
          market.languages = (market.languages || []).map((language) => ({
            ...language,
            flagUrl: language.flagUrl || getLanguageFlagUrl(language.iso),
          }));
        });

        return config;
      } catch (error) {
        return {};
      }
    }

    _showTransition(animate = true) {
      this.style.display = 'flex';
      if (!animate || !this.animate) {
        this.style.opacity = '1';
        return Promise.resolve();
      }

      return this.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 200,
        fill: 'forwards',
      }).finished;
    }

    _hideTransition() {
      if (!this.animate) {
        this.style.display = 'none';
        return Promise.resolve();
      }

      return this.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 150,
        fill: 'forwards',
      }).finished.then(() => {
        this.style.display = 'none';
      });
    }

    _maybeAutoShow() {
      if (this._geolocationDismissedThisPage) {
        return;
      }

      if (localStorage.getItem('theme:geolocation-confirmed') === 'true') {
        return;
      }

      this._detectCountryMismatch().then((detectedCountry) => {
        if (!detectedCountry || this._geolocationDismissedThisPage) {
          return;
        }

        this._onCountryChange(detectedCountry);

        window.clearTimeout(this._geolocationAutoShowTimerId);
        this._geolocationAutoShowTimerId = window.setTimeout(() => {
          if (!this._geolocationDismissedThisPage && !this.open) {
            this.show();
          }
        }, 400);
      });
    }

    // resolves to the detected country ISO when it differs from the current
    // market and is one of the configured markets, otherwise null
    async _detectCountryMismatch() {
      const currentCountry = (this.dataset.currentCountry || '').toUpperCase();

      let detected = sessionStorage.getItem('geolocation-detected-country');

      if (detected === null) {
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(
            `${root}browsing_context_suggestions.json?source=geolocation_recommendation&country%5Benabled%5D=true&language%5Benabled%5D=true`
          );
          const data = response.ok ? await response.json() : null;
          detected = (data?.detected_values?.country?.handle || '').toUpperCase();
        } catch (error) {
          detected = '';
        }

        try {
          sessionStorage.setItem('geolocation-detected-country', detected);
        } catch (error) { }
      }

      if (!detected || detected === currentCountry) {
        return null;
      }

      return this._marketConfig[detected] ? detected : null;
    }

    _onCountryChange(countryIso) {
      if (!countryIso || !this._marketConfig[countryIso]) {
        return;
      }

      if (this._countryInput) {
        this._countryInput.value = countryIso;
      }

      if (this._countryLabel) {
        this._countryLabel.textContent = this._marketConfig[countryIso].label;
      }

      this._updateCountryIcon(this._countryFlag, countryIso);

      const languages = this._marketConfig[countryIso].languages || [];
      const preferredLocale =
        languages.find((language) => language.iso === this.dataset.currentLocale)
          ?.iso || languages[0]?.iso;

      this._updateLanguageIcon(this._languageFlag, preferredLocale);
      this._renderLanguageOptions(countryIso, preferredLocale);
    }

    _onLanguageChange(localeIso) {
      if (!localeIso || !this._localeInput) {
        return;
      }

      this._localeInput.value = localeIso;

      if (this._languageLabel) {
        const countryIso = this._countryInput?.value;
        const language = this._marketConfig[countryIso]?.languages?.find(
          (entry) => entry.iso === localeIso
        );
        this._languageLabel.textContent = getLanguageDisplayName(
          localeIso,
          language?.name
        );
      }

      this._updateLanguageIcon(this._languageFlag, localeIso);
    }

    _renderLanguageOptions(countryIso, selectedLocale) {
      if (!this._languageListbox) {
        return;
      }

      const languages = this._marketConfig[countryIso]?.languages || [];
      const fragment = document.createDocumentFragment();

      languages.forEach((language) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'geolocation-popup__listbox-option';
        option.setAttribute('role', 'option');
        option.value = language.iso;
        option.id = `geolocation-language-option-${language.iso}`;
        option.title = getLanguageDisplayName(language.iso, language.name);

        option.insertAdjacentHTML('afterbegin', getLanguageFlagHtml(language));

        const label = document.createElement('span');
        label.textContent = getLanguageDisplayName(language.iso, language.name);
        option.appendChild(label);

        if (language.iso === selectedLocale) {
          option.setAttribute('aria-selected', 'true');
        }

        fragment.appendChild(option);
      });

      this._languageListbox.replaceChildren(fragment);
      this._languageListbox.setAttribute(
        'aria-activedescendant',
        `geolocation-language-option-${selectedLocale}`
      );

      if (selectedLocale) {
        this._onLanguageChange(selectedLocale);
      }
    }
  }

  if (!window.customElements.get('geolocation-popup')) {
    window.customElements.define('geolocation-popup', GeolocationPopup);
  }
})();
